import { Children, FC } from 'react';
import { useTranslation } from 'next-i18next';
import { cx } from '@emotion/css';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';

import { PhoenixIcon } from '../phoenix-icon/phoenix-icon';
import style from './slider.module.scss';

export const Slider: FC<SwiperProps & { sliderOptions: SwiperOptions; dataTestId?: string; containerClassName?: string }> = ({
  id,
  sliderOptions,
  pagination,
  children,
  dataTestId = 'slider',
  containerClassName,
  ...swiperProps
}) => {
  const { t } = useTranslation('shared');
  const { spaceBetween, slidesPerView, breakpoints, slidesPerGroup } = sliderOptions;
  const arrayChildren = Children.toArray(children);

  const sliderHasSingleChild = arrayChildren.length <= 1;
  const sliderHasMultipleViews = arrayChildren.length > Number(slidesPerView);

  return (
    <div className={cx('relative', containerClassName)}>
      <Swiper
        modules={[Navigation, Pagination, A11y]}
        id={id}
        observer={true}
        observeParents={true}
        spaceBetween={spaceBetween}
        slidesPerView={sliderHasSingleChild ? 1 : slidesPerView}
        slidesPerGroup={slidesPerGroup ?? 1}
        breakpoints={sliderHasSingleChild ? {} : breakpoints}
        watchOverflow={!sliderHasSingleChild}
        pagination={
          typeof pagination === 'boolean'
            ? pagination
            : {
                el: `.pagination_${id}`,
                dynamicBullets: true,
                ...pagination,
              }
        }
        navigation={{
          nextEl: `.nav-next_${id}`,
          prevEl: `.nav-prev_${id}`,
        }}
        data-testid={dataTestId}
        a11y={{
          enabled: true,
          prevSlideMessage: t('shared_slider_previousSlide') ?? '',
          nextSlideMessage: t('shared_slider_nextSlide') ?? '',
        }}
        {...swiperProps}
      >
        {Children.map(arrayChildren, (child, index) => {
          return (
            <SwiperSlide key={index} id={index.toString()} className="!h-auto select-none self-stretch">
              {child}
            </SwiperSlide>
          );
        })}
      </Swiper>
      {sliderHasMultipleViews && (
        <>
          <div className={cx(style.pagination, `pagination_${id}`)} />
          <div className={cx(style.swiper_button_prev, `swiper-button-prev nav-prev_${id}`)} data-testid="arrow-left">
            <PhoenixIcon icon={getDesignSystemIcon('icon-arrow-left-16.svg')} />
          </div>
          <div className={cx(style.swiper_button_next, `swiper-button-next nav-next_${id}`)} data-testid="arrow-right">
            <PhoenixIcon icon={getDesignSystemIcon('icon-arrow-right-16.svg')} />
          </div>
        </>
      )}
    </div>
  );
};
